// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import InterviewResults from './pages/admin/InterviewResults';

// In the admin routes section, add this route:


// Pages
import LoginPage from './pages/auth/LoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import JobDescriptions from './pages/admin/JobDescriptions';
import ResumeManagement from './pages/admin/ResumeManagement';
import InterviewManagement from './pages/admin/InterviewManagement';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import CandidateDashboard from './pages/candidate/CandidateDashboard';
import CandidateInterviews from './pages/candidate/CandidateInterviews';
import InterviewRoom from './pages/interview/InterviewRoom';
import CandidateNotifications from './pages/candidate/CandidateNotifications';
import DirectInterview from './pages/public/DirectInterview';

// Layout Components
import AdminLayout from './components/shared/layout/AdminLayout';
import CandidateLayout from './components/shared/layout/CandidateLayout';
import ErrorBoundary from './components/shared/ErrorBoundary';

import MockInterviewSetup from './pages/candidate/MockInterviewSetup';


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('React Query error:', error);
        // Ensure error is properly formatted
        if (typeof error === 'object' && !error.message) {
          console.error('Error object without message:', error);
        }
      },
    },
    mutations: {
      onError: (error) => {
        console.error('React Query mutation error:', error);
        // Ensure error is properly formatted
        if (typeof error === 'object' && !error.message) {
          console.error('Mutation error object without message:', error);
        }
      },
    },
  },
});

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Public routes that don't require authentication
  return (
    <Routes>
      {/* Public direct interview route */}
      <Route path="/interview/direct/:token" element={<DirectInterview />} />
      
      {/* Authentication required routes */}
      {!user ? (
        <>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      ) : user.role === 'admin' ? (
        <>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="jobs" element={<JobDescriptions />} />
            <Route path="resumes" element={<ResumeManagement />} />
            <Route path="interviews" element={<InterviewManagement />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="interviews/:interviewId/results" element={<InterviewResults />} />
          </Route>
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </>
      ) : (
        <>
          <Route path="/candidate" element={<CandidateLayout />}>
            <Route index element={<CandidateDashboard />} />
            <Route path="interviews" element={<CandidateInterviews />} />
            <Route path="practice-interview" element={<MockInterviewSetup />} />
            <Route path="notifications" element={<CandidateNotifications />} />
          </Route>
          <Route path="/interview/:interviewId" element={<InterviewRoom />} />
          <Route path="*" element={<Navigate to="/candidate" replace />} />
        </>
      )}
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;